import {
  createFileRoute,
  redirect,
  useLoaderData,
  useRouter,
} from "@tanstack/react-router";
import { useState, type JSX } from "react";
import { z } from "zod";

import { graphql } from "@repo/graphql-types/gql";
import {
  CalendarPageAppointmentsQueryDocument,
  CalendarPageFreeSlotsQueryDocument,
  RecurringAppointmentsPageQueryDocument,
  WaitingRoomPageQueryDocument,
} from "@repo/graphql-types/graphql";
import Calendar from "@repo/icons/default/Calendar";

import { AppointmentCancelSummaryCard } from "@/components/appointment/drawer/appointment-cancel-summary-card";
import { AppointmentCancelledDrawer } from "@/components/appointment/drawer/appointment-cancelled-drawer";
import {
  type RecurrenceScope,
  RecurrenceScopeChooser,
} from "@/components/recurring-appointment/recurrence-scope-chooser";
import {
  MessageDrawerActionButton,
  MessageDrawerActions,
  MessageDrawerBody,
  MessageDrawerRoot,
  MessageDrawerTitle,
} from "@/components/ui/drawer/message-drawer";
import {
  ensureQueryData,
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
} from "@/hooks/use-graphql";
import { useMarkAppointmentsAsCancelled } from "@/hooks/use-mark-appointments-as-cancelled";
import {
  isAppointment,
  isRecurrenceAppointmentFrom,
} from "@/lib/helpers/appointment-cache";
import {
  capitalizeMonth,
  convertDateTimeIgnoringTimezone,
  formatIgnoringTimezone,
  removeSecondsFromTimeString,
} from "@/lib/helpers/date";
import { trackEvent } from "@/lib/tracking";
import { logError } from "@/lib/utils/log-error";

const ROUTE_ID = "/appointment/$appointmentId/cancel-recurrence";

const AppointmentCancelRecurrenceScopeQuery = graphql(`
  query AppointmentCancelRecurrenceScopeQuery($codAgendamento: Int!) {
    agendamento: agendamentos_by_pk(codAgendamento: $codAgendamento) {
      codAgendamento
      codAgendamentoRecorrente
      data
      horaInicio
      ...AppointmentCancelSummaryCardFragment
    }
  }
`);

const RecurrenceUpcomingAppointmentsQuery = graphql(`
  query RecurrenceUpcomingAppointmentsQuery(
    $codAgendamentoRecorrente: Int!
    $data: date!
  ) {
    agendamentos(
      where: {
        codAgendamentoRecorrente: { _eq: $codAgendamentoRecorrente }
        cancelado: { _eq: false }
        data: { _gte: $data }
      }
    ) {
      codAgendamento
    }
  }
`);

const CancelSingleAppointmentMutation = graphql(`
  mutation CancelRecurringSingleAppointmentMutation(
    $codAgendamento: Int!
    $origem: String = "discovery"
  ) {
    LivanceApiAgendamentoCancela(codAgendamento: $codAgendamento, origem: $origem) {
      codAgendamento
    }
  }
`);

const CancelRecurrenceMutation = graphql(`
  mutation CancelRecurrenceMutation($input: CancelarRecorrenciaInput!) {
    cancelarRecorrencia(input: $input) {
      sucesso
      errors {
        ... on ValidationError {
          __typename
          message
        }
      }
    }
  }
`);

const AppointmentCancelRecurrencePage = (): JSX.Element => {
  const { appointment, upcomingCount } = useLoaderData({ from: ROUTE_ID });
  const router = useRouter();

  const codAgendamento = appointment.codAgendamento;
  const codAgendamentoRecorrente = appointment.codAgendamentoRecorrente ?? 0;

  const [scope, setScope] = useState<RecurrenceScope>("single");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [cancelledScope, setCancelledScope] = useState<RecurrenceScope | null>(null);

  const dateObject = appointment.data
    ? convertDateTimeIgnoringTimezone(appointment.data)
    : null;
  const dayAndMonth = dateObject
    ? capitalizeMonth(formatIgnoringTimezone(dateObject, "d 'de' MMMM"))
    : "";
  const startTime = appointment.horaInicio
    ? removeSecondsFromTimeString(appointment.horaInicio)
    : "";

  const markAppointmentsAsCancelled = useMarkAppointmentsAsCancelled();
  const invalidateCalendarAppointments = useInvalidateQuery(
    CalendarPageAppointmentsQueryDocument,
  );
  const invalidateCalendarFreeSlots = useInvalidateQuery(
    CalendarPageFreeSlotsQueryDocument,
  );
  const invalidateWaitingRoom = useInvalidateQuery(WaitingRoomPageQueryDocument);
  const invalidateRecurringAppointments = useInvalidateQuery(
    RecurringAppointmentsPageQueryDocument,
  );

  const onCloseErrorDrawer = (): void => {
    setIsConfirmOpen(false);
  };

  const { mutateAsync: cancelSingle, isPending: isCancelingSingle } =
    useGraphQLMutationWithErrorHandler(CancelSingleAppointmentMutation, {
      onCloseErrorDrawer,
    });
  const { mutateAsync: cancelRecurrence, isPending: isCancelingRecurrence } =
    useGraphQLMutationWithErrorHandler(CancelRecurrenceMutation, {
      onCloseErrorDrawer,
    });

  const isPending = isCancelingSingle || isCancelingRecurrence;

  const onSuccess = (): void => {
    markAppointmentsAsCancelled(
      scope === "all"
        ? isRecurrenceAppointmentFrom(
            codAgendamento,
            codAgendamentoRecorrente,
            appointment.data,
          )
        : isAppointment(codAgendamento),
    );
    invalidateCalendarAppointments();
    invalidateCalendarFreeSlots();
    invalidateWaitingRoom();
    invalidateRecurringAppointments();

    trackEvent("Agendamento cancelado", {
      agendamento: codAgendamento,
      tipo: scope === "all" ? "recorrente-todos" : "recorrente-um",
      recorrencia: codAgendamentoRecorrente,
    });

    // Troca a confirmação pela mensagem de sucesso; a navegação de volta só
    // acontece quando o membro conclui.
    setIsConfirmOpen(false);
    setCancelledScope(scope);
  };

  const onError = (error: Error): void => {
    logError(error, { codAgendamento, codAgendamentoRecorrente });
  };

  const handleBack = (): void => {
    router.history.back();
  };

  const handleContinueScope = (): void => {
    trackEvent("Cancelar recorrência escopo selecionado", {
      agendamento: codAgendamento,
      escopo: scope,
    });
    setIsConfirmOpen(true);
  };

  const handleConfirm = async (): Promise<void> => {
    trackEvent("Cancelar recorrência solicitado", {
      agendamento: codAgendamento,
      escopo: scope,
      recorrencia: codAgendamentoRecorrente,
    });

    if (scope === "all") {
      await cancelRecurrence(
        {
          input: {
            codAgendamentoRecorrente,
            dataInicioCancelamento: appointment.data,
          },
        },
        { onSuccess, onError },
      );
      return;
    }

    await cancelSingle({ codAgendamento }, { onSuccess, onError });
  };

  const confirmTitle =
    scope === "all"
      ? `Cancelar ${upcomingCount} agendamentos?`
      : "Cancelar este agendamento?";
  const confirmScopeMessage =
    scope === "all"
      ? `Todos os próximos ${upcomingCount} agendamentos da recorrência, a partir de ${dayAndMonth}, serão cancelados.`
      : "Os próximos agendamentos da recorrência continuam na sua agenda.";
  const confirmButtonLabel =
    scope === "all"
      ? `Sim, cancelar ${upcomingCount} agendamentos`
      : "Sim, cancelar agendamento";

  const cancelledTitle =
    cancelledScope === "all" ? "Agendamentos cancelados" : "Agendamento cancelado";
  const cancelledDescription =
    cancelledScope === "all"
      ? `${upcomingCount} agendamentos da recorrência foram cancelados a partir de ${dayAndMonth} e o paciente recebeu um e-mail avisando do cancelamento.`
      : "O horário foi liberado na sua agenda e o paciente recebeu um e-mail avisando do cancelamento.";

  return (
    <>
      <RecurrenceScopeChooser
        title="Cancelar agendamento"
        subtitle="Escolha se deseja cancelar apenas este agendamento ou todos os seguintes"
        singleLabel="Apenas este agendamento"
        singleDescription={dayAndMonth ? `${dayAndMonth} · ${startTime}` : undefined}
        allLabel={`Todos os próximos ${upcomingCount} agendamentos`}
        allDescription={dayAndMonth ? `A partir de ${dayAndMonth}` : undefined}
        value={scope}
        onChange={setScope}
        onContinue={handleContinueScope}
        onBack={handleBack}
      />

      <MessageDrawerRoot
        icon={Calendar}
        open={isConfirmOpen}
        setOpen={setIsConfirmOpen}
        variant="secondary"
      >
        <MessageDrawerTitle>{confirmTitle}</MessageDrawerTitle>
        <MessageDrawerBody>
          <div className="flex flex-col gap-4">
            <AppointmentCancelSummaryCard
              appointment={appointment}
              recurring={scope === "all"}
            />
            <p>{confirmScopeMessage}</p>
            <p>O paciente será avisado por e-mail sobre o cancelamento.</p>
          </div>
        </MessageDrawerBody>
        <MessageDrawerActions>
          <MessageDrawerActionButton
            data-testid="confirm-cancel-button"
            loading={isPending}
            onClick={() => void handleConfirm()}
          >
            {confirmButtonLabel}
          </MessageDrawerActionButton>
          <MessageDrawerActionButton
            secondary
            disabled={isPending}
            onClick={() => setIsConfirmOpen(false)}
          >
            Voltar
          </MessageDrawerActionButton>
        </MessageDrawerActions>
      </MessageDrawerRoot>

      <AppointmentCancelledDrawer
        open={cancelledScope !== null}
        appointment={appointment}
        title={cancelledTitle}
        recurring={cancelledScope === "all"}
        description={cancelledDescription}
        // Volta também a etapa de onde o cancelamento foi aberto (drawer de detalhes
        // ou menu da sala de espera), levando o membro para a tela de origem.
        onConclude={() => router.history.go(-2)}
      />
    </>
  );
};

export const Route = createFileRoute("/appointment/$appointmentId/cancel-recurrence")({
  component: AppointmentCancelRecurrencePage,
  parseParams: (params) => ({
    appointmentId: z.int().parse(Number(params.appointmentId)),
  }),
  loader: async (opts) => {
    const data = await ensureQueryData(
      opts.context,
      AppointmentCancelRecurrenceScopeQuery,
      { codAgendamento: opts.params.appointmentId },
    );

    const appointment = data.agendamento;

    // Só faz sentido para agendamentos recorrentes; caso contrário volta à agenda.
    if (appointment?.codAgendamentoRecorrente == null) {
      throw redirect({ to: "/calendar" });
    }

    const upcoming = await ensureQueryData(
      opts.context,
      RecurrenceUpcomingAppointmentsQuery,
      {
        codAgendamentoRecorrente: appointment.codAgendamentoRecorrente,
        data: appointment.data,
      },
    );

    return { appointment, upcomingCount: upcoming.agendamentos.length };
  },
});
